/**
 * Publishes the on-screen keyboard's height to CSS as `--kb-inset`.
 *
 * Chrome's default on Android is `interactive-widget: resizes-visual`: the keyboard
 * shrinks the *visual* viewport and leaves the layout viewport alone. So `100dvh`,
 * `position: sticky` and the document's scroll height all still believe the screen is
 * its full height, and anything the page puts near the bottom is drawn underneath the
 * keyboard. The only thing that knows better is `visualViewport`.
 *
 * Rather than have every component subscribe, the height lands in one custom property
 * on the root element and CSS does the rest. Returns its own teardown.
 */
export function trackKeyboardInset(): () => void {
  const viewport = window.visualViewport
  const root = document.documentElement

  // jsdom, and any browser old enough to lack the API: the inset stays at its 0px
  // default, every `calc()` that reads it still resolves, and nothing breaks.
  if (viewport === null || viewport === undefined) return () => {}

  const target = viewport

  const update = () => {
    // Pinch-zoom shrinks the visual viewport too, and that is not a keyboard — a reader
    // who has zoomed in to read wants the layout left exactly where it is.
    const inset =
      target.scale > 1 ? 0 : Math.max(0, window.innerHeight - target.height - target.offsetTop)

    root.style.setProperty('--kb-inset', `${Math.round(inset)}px`)
  }

  update()
  // `scroll` matters as much as `resize`: the viewport slides under a keyboard that is
  // already open when the caret moves, which changes the offset without changing height.
  target.addEventListener('resize', update)
  target.addEventListener('scroll', update)

  return () => {
    target.removeEventListener('resize', update)
    target.removeEventListener('scroll', update)
  }
}
